import { cn } from "@/components/podkit/lib/cn";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { useDebouncedCallback, useThrottledCallback } from "use-debounce";

type ScrollAreaProps = {
    children: ReactNode;
    orientation?: "horizontal" | "vertical" | "both";
    // Note: Don't use this to add padding or layout properties. Apply those styles to the child element instead.
    className?: string;
    // Note: Auto-scroll is currently only supported for the vertical orientation
    autoScroll?: boolean;
    autoScrollContentRef?: React.RefObject<HTMLDivElement>;
    /**
     * Callback function that gets triggered when the user scrolls to the end of the content
     */
    onScrollEnd?: () => void;
    /**
     * Threshold in pixels from the bottom to trigger onScrollEnd (default: 50)
     */
    scrollEndThreshold?: number;
};

export const ScrollArea: React.FC<ScrollAreaProps> = ({
    children,
    orientation,
    className,
    autoScroll = false,
    autoScrollContentRef,
    onScrollEnd,
    scrollEndThreshold = 50,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const prevHeightRef = useRef(0);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(autoScroll);
    const lastScrollTopRef = useRef(0);

    const debouncedScroll = useThrottledCallback((scrollContainer: HTMLDivElement, scrollTop: number) => {
        scrollContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
    }, 500);

    // Update isAutoScrollEnabled when the prop changes
    useEffect(() => {
        setIsAutoScrollEnabled(autoScroll);
    }, [autoScroll]);

    useEffect(() => {
        if (!isAutoScrollEnabled || !scrollContainerRef.current || !autoScrollContentRef?.current) return;

        // We're relying on the ResizeObserver to detect height changes of the content, and based on that,
        // trigger auto-scroll to the bottom of the container.
        const resizeObserver = new ResizeObserver(() => {
            if (!scrollContainerRef.current || !autoScrollContentRef.current) {
                return;
            }

            // Only scroll if height has increased
            const newHeight = autoScrollContentRef.current.scrollHeight;
            if (newHeight > prevHeightRef.current) {
                const container = scrollContainerRef.current;
                if (!container) return;
                debouncedScroll(container, newHeight);
            }
            prevHeightRef.current = newHeight;
        });

        // Start observing the container's content
        resizeObserver.observe(autoScrollContentRef.current);

        // Initial scroll to bottom
        debouncedScroll(scrollContainerRef.current, scrollContainerRef.current.scrollHeight);

        // Clean up the observer
        return () => {
            resizeObserver.disconnect();
        };
    }, [isAutoScrollEnabled, debouncedScroll, autoScrollContentRef]);

    const debouncedOnScrollEnd = useDebouncedCallback(
        () => {
            onScrollEnd?.();
        },
        500,
        {
            leading: true,
            trailing: false,
        },
    );

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const bottom = scrollHeight - clientHeight;

        // Check if we're near the end of the scroll area and trigger the callback if provided
        if (onScrollEnd && scrollTop + clientHeight >= scrollHeight - scrollEndThreshold) {
            debouncedOnScrollEnd();
        }

        // Disable auto scroll if the user scrolls up
        if (scrollTop < lastScrollTopRef.current) {
            setIsAutoScrollEnabled(false);
            // Cancel any pending/throttled autoscroll events.
            debouncedScroll.cancel();
        }
        // Re-enable auto scroll if the user scrolls to the bottom
        else if (scrollTop >= bottom) {
            setIsAutoScrollEnabled(true);
        }

        lastScrollTopRef.current = scrollTop;
    }, [debouncedOnScrollEnd, debouncedScroll, onScrollEnd, scrollEndThreshold]);

    return (
        <div
            ref={scrollContainerRef}
            id="scrollable"
            className={cn(
                {
                    // scrollbar-gutter:stable ensures we always reserve space for the scrollbar.
                    // This is important for layout stability, as it means content won't jump around when e.g. resizing the window causes scrollbars to appear/disappear.
                    // However, it seems to only work for vertical scrollbars.
                    "[scrollbar-gutter:stable]": orientation === "vertical",
                    "overflow-x-auto overflow-y-hidden pb-[3px]": orientation === "horizontal",
                    "overflow-y-auto overflow-x-hidden pr-[3px]": orientation === "vertical",
                    "overflow-x-auto overflow-y-auto pb-[3px] pr-[3px]": orientation === "both",
                },
                className,
            )}
            onScroll={handleScroll}
            data-orientation={orientation}
        >
            {children}
        </div>
    );
};
